<template>
    <div id="event_list">
        <EspSnackbar v-model="snackbarShow" :color="snackbarColor" :text="snackbarText" :timeout="6000"/>
        <HLConfirm v-model="gpsDialog" v-bind="gpsDialogOptions"/>
        <v-card flat>
            <v-card v-if="!hideLogo" flat class="d-flex">
                <v-card flat class="mx-auto">
                    <!-- Logo -->
                    <img class="pt-10" :src="`${this.customBasePath}/logo.png`" style="max-width:100%;max-height:100px" />
                </v-card>
            </v-card>
            <!-- Tabs: my events always, public events only when there are some -->
            <v-tabs v-if="hasAnyEvents && !isLoadingEvents" v-model="tab" grow class="mt-6">
                <v-tab href="#my">{{ $t('eventsMy', { eventPlural: $tc('event', 2) }) }}</v-tab>
                <v-tab v-if="hasPublicEvents" href="#public">{{ $t('eventsPublic', { eventPlural: $tc('event', 2) }) }}</v-tab>
            </v-tabs>

            <v-card-text class="pa-0">
                <v-container class="pa-0">
                    <!-- Loading mask -->
                    <HLLoadingMask v-if="isLoadingEvents" />
                    <!-- List-->
                    <div v-else>
                        <EspError v-if="error != null" icon="mdi-alert-decagram" iconColor="red">
                            {{ $t('errorAlertConnectionProblems') }}
                            <template #subTitle>{{ $t(`info_checkin_error_${error}`) }}</template>
                        </EspError>
                        <div v-else>
                            <v-list v-if="currentRows.length > 0" three-line class="text-left pt-0">
                                <template v-for="section in sections">
                                    <template v-if="section.events.length > 0">
                                        <v-subheader :key="`${section.key}-header`" class="section-header">{{ section.label }}</v-subheader>
                                        <template v-for="(item, index) in section.events">
                                            <v-list-item
                                                :key="`${section.key}-${item.id_event}`"
                                                :ripple="false"
                                                :style="isCurrentRow(item) ? currentEventStyle : null"
                                                @click="checkintoEvent(item.id_event)"
                                            >
                                                <div class="event">
                                                    <div class="first-row">
                                                        <div class="img-container">
                                                            <v-img
                                                                class="image"
                                                                v-if="item.bg_event"
                                                                :src="backgroundEventListImageUrl(item.bg_event)"
                                                            >
                                                            </v-img>
                                                            <v-img class="image" v-else src="/img/pictures/event-no-picture.png"> </v-img>
                                                        </div>
                                                        <div class="content">
                                                            <div class="title-container">
                                                                <div class="title">
                                                                    {{ item.evnamedt }}
                                                                    <span class="caption">({{ item.id_event }})</span>
                                                                </div>
                                                                <div class="icon-right">
                                                                    <v-icon :color="chevronColor(item)" x-large>mdi-chevron-right</v-icon>
                                                                </div>
                                                            </div>
                                                            <!-- Time left only while active, like the native app -->
                                                            <div v-if="section.key === 'active'" class="countdown">{{ timeLeftLabel(item) }}</div>
                                                            <div v-if="usecaseLabel(item)" class="usecase" :style="{ color: appTheme.footerIconColor }">
                                                                <v-icon small :color="appTheme.footerIconColor">mdi-label</v-icon>
                                                                <span class="caption ml-1">{{ usecaseLabel(item) }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="second-row">
                                                        <div class="event-description">
                                                            <p>{{ item.evcommentdt }}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </v-list-item>
                                            <v-divider v-if="index < section.events.length - 1" :key="`${section.key}-${item.id_event}-divider`"></v-divider>
                                        </template>
                                    </template>
                                </template>
                            </v-list>
                            <EspError v-else icon="mdi-alert-decagram">
                                {{ $t('app_eventlist_empty') }}
                                <template #subTitle>{{ $t('app_eventlist_empty_msg') }}</template>
                            </EspError>
                        </div>
                    </div>
                </v-container>
            </v-card-text>

            <!-- Refresh and sort on the left, PIN and QR on the right (same order as the task list actions) -->
            <v-card-actions class="list-actions px-4 pb-4">
                <v-btn elevation="0" fab small dark color="grey lighten-1" :disabled="isLoadingEventsImmediately" @click="loadEvents">
                    <v-icon>mdi-cached</v-icon>
                </v-btn>
                <v-menu v-if="hasAnyEvents" offset-y top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" elevation="0" fab small dark color="grey lighten-1" class="ml-3">
                            <v-icon>{{ sortIcon }}</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="option in sortOptions"
                            :key="option.key"
                            link
                            :input-value="option.key === sortOption"
                            @click="onChangeOrder(option.key)"
                        >
                            <v-list-item-icon>
                                <v-icon>{{ option.icon[option.key === sortOption ? sortOrder : option.defaultOrder] }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>{{ option.label }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-spacer></v-spacer>
                <EspButtonPinCheckin v-if="!disablePINAccess" class="mr-6" @success="onCodeReceived" />
                <EspButtonQrCheckin
                    v-if="!disableQRAccess"
                    icon="mdi-qrcode"
                    :label="$t('qrCode')"
                    :fctIsValidCode="isScannedCodeValid"
                    @success="onCodeReceived"
                />
            </v-card-actions>

            <!-- Loading mask for checkin -->
            <HLLoadingMaskOverlay v-if="event_single_loading" />
        </v-card>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import CONFIG from '../../constants/config';
import { layoutMixin } from '../../utils/layout-mixin';
import { settingsMixin } from '../../utils/settings-mixin';
import { eventMixin } from '../../utils/event-mixin';
import { qrLoginMixin } from '../../utils/qrlogin-mixin';
import { qrJoinMixin } from '../../utils/qr-join-mixin';
import { geolocationMixin } from '../../utils/geolocation-mixin';
import { onboardingMixin } from '../../utils/onboarding-mixin';
import {
    DEFAULT_SORT_ORDER,
    eventTimeLeftMillis,
    formatDuration,
    groupEvents,
    isCurrentEvent,
    sortEvents,
    visibleRows,
} from '../../utils/event-list.mjs';
import { distanceOf } from '../../utils/geo-distance.mjs';
import HLLoadingMask from '../ui/HLLoadingMask.vue';
import HLLoadingMaskOverlay from '../ui/HLLoadingMaskOverlay.vue';
import EspError from '../animation/EspError.vue';
import EspSnackbar from '../ui/EspSnackbar.vue';
import HLConfirm from '../ui/HLConfirm.vue';
import EspButtonPinCheckin from '../auth/EspButtonPinCheckin.vue';
import EspButtonQrCheckin from '../spots/EspButtonQrCheckin.vue';

export default {
    mixins: [settingsMixin, eventMixin, layoutMixin, qrLoginMixin, qrJoinMixin, geolocationMixin, onboardingMixin],
    components: {
        HLLoadingMask,
        HLLoadingMaskOverlay,
        EspError,
        EspSnackbar,
        HLConfirm,
        EspButtonPinCheckin,
        EspButtonQrCheckin,
    },
    props: {
        aspectRatioProp: {},
        // leave out the build logo above the list where the surrounding page already shows it
        hideLogo: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            error: null,
            tab: 'my',
            now: Date.now(),
            nowTimer: null,
            snackbarShow: false,
            snackbarText: '',
            snackbarColor: 'error',
            aspectRatioValue: this.aspectRatioProp,
        };
    },
    created() {
        this.loadEvents();
    },
    mounted() {
        this.nowTimer = setInterval(() => {
            this.now = Date.now();
        }, 1000);
        window.addEventListener('resize', this.calculateRatio);
        this.calculateRatio();
        if (this.sortOption === 'byDistance') {
            this.ensureDistanceSort();
        }
    },
    beforeDestroy() {
        clearInterval(this.nowTimer);
        window.removeEventListener('resize', this.calculateRatio);
    },
    watch: {
        // QR data in the URL (deep link, login redirect or a scan on this page)
        '$route.query.d': {
            handler(val) {
                if (val) {
                    this.readEvent();
                }
            },
            immediate: true,
        },
        hasPublicEvents(val) {
            if (!val) {
                this.tab = 'my';
            }
        },
    },
    computed: {
        ...mapState('events', { sortOption: 'event_sort_option', sortOrder: 'event_sort_order' }),
        groups() {
            return groupEvents(this.allEvents, {
                nowMs: this.now,
                parseInTz: this.parseInEventTimezone,
                hideMain: this.hideMainEvent,
                hideInactive: this.hideInactiveEvents,
            });
        },
        hasMyEvents() {
            return this.groups.my.active.length + this.groups.my.expired.length > 0;
        },
        hasPublicEvents() {
            return this.groups.public.active.length + this.groups.public.expired.length > 0;
        },
        hasAnyEvents() {
            return visibleRows(this.groups).length > 0;
        },
        currentTab() {
            return this.tab === 'public' && this.hasPublicEvents ? 'public' : 'my';
        },
        sections() {
            const group = this.groups[this.currentTab];
            return [
                {
                    key: 'active',
                    label: this.$t('eventsActive', { eventPlural: this.$tc('event', 2) }),
                    events: this.sortedRows(group.active),
                },
                {
                    key: 'expired',
                    label: this.$t('eventsExpired', { eventPlural: this.$tc('event', 2) }),
                    events: this.sortedRows(group.expired),
                },
            ];
        },
        currentRows() {
            return [].concat(...this.sections.map((section) => section.events));
        },
        distanceSortEnabled() {
            return !this.parseBoolean(CONFIG.DISABLE_EVENT_SORT_BY_DISTANCE, false);
        },
        sortOptions() {
            const options = [
                {
                    key: 'byLastUsed',
                    icon: { asc: 'mdi-sort-clock-descending', desc: 'mdi-sort-clock-ascending' },
                    label: this.$t('sortByCheckin'),
                },
                {
                    key: 'byName',
                    icon: { asc: 'mdi-sort-alphabetical-ascending', desc: 'mdi-sort-alphabetical-descending' },
                    label: this.$t('sortByName'),
                },
                {
                    key: 'byID',
                    icon: { asc: 'mdi-sort-numeric-ascending', desc: 'mdi-sort-numeric-descending' },
                    label: this.$t('sortByNumber'),
                },
                {
                    key: 'byStartTime',
                    icon: { asc: 'mdi-sort-calendar-ascending', desc: 'mdi-sort-calendar-descending' },
                    label: this.$t('sortByStartTime'),
                },
            ];
            if (this.distanceSortEnabled) {
                options.push({
                    key: 'byDistance',
                    icon: { asc: 'mdi-sort-ascending', desc: 'mdi-sort-descending' },
                    label: this.$t('sortByDistance'),
                });
            }
            return options.map((option) => ({ ...option, defaultOrder: DEFAULT_SORT_ORDER[option.key] }));
        },
        currentSortOption() {
            return this.sortOptions.find((option) => option.key === this.sortOption) || this.sortOptions[0];
        },
        sortIcon() {
            return this.currentSortOption.icon[this.sortOrder];
        },
        durationLabels() {
            return {
                d: this.$t('daysShort'),
                h: this.$t('hoursShort'),
                m: this.$t('minutesShort'),
                s: this.$t('secondsShort'),
            };
        },
        // header colours like the native app; a white header would vanish on the white card
        currentEventThemed() {
            return !this.isWhiteColor(this.appTheme.headerFooterColor);
        },
        currentEventStyle() {
            return this.currentEventThemed
                ? { backgroundColor: this.appTheme.headerFooterColor, color: this.appTheme.footerIconColor }
                : { backgroundColor: 'rgba(0, 0, 0, 0.06)' };
        },
    },
    methods: {
        // always fresh from the server (past the GET cache): the list is reloaded on every open, like the native app
        loadEvents() {
            return this.getEvents({ gametypes: this.availableGametypes, force: true })
                .then(() => {
                    // open the first tab that has something to show
                    if (!this.hasMyEvents && this.hasPublicEvents) {
                        this.tab = 'public';
                    }
                })
                .catch((ex) => {
                    console.warn('getEvents failed', ex);
                });
        },
        sortedRows(list) {
            return sortEvents(list, this.sortOption, this.sortOrder, {
                parseInTz: this.parseInEventTimezone,
                distanceOf: distanceOf(this.position),
            });
        },
        timeLeftLabel(item) {
            const time = formatDuration(eventTimeLeftMillis(item, this.now, this.parseInEventTimezone), this.durationLabels);
            return time ? this.$t('eventTimeLeft', { time }) : '';
        },
        usecaseLabel(item) {
            return item && item.usecase ? String(item.usecase).trim() : '';
        },
        isCurrentRow(item) {
            return isCurrentEvent(item, this.selectedEventId);
        },
        chevronColor(item) {
            return this.isCurrentRow(item) && this.currentEventThemed ? this.appTheme.footerIconColor : 'grey';
        },
        isWhiteColor(color) {
            const value = String(color || '').trim().toLowerCase();
            return value === '#fff' || value === '#ffffff' || value === 'white';
        },
        checkintoEvent(event_id) {
            this.$emit('next', event_id);
        },
        readEvent() {
            let qrAtStart = this.getQrData();
            this.error = null;
            this.readEventWithQR(qrAtStart)
                .then((eventId) => {
                    this.$emit('next', eventId);
                })
                .catch((ex) => {
                    let err =
                        ex && ex.response && ex.response.data && ex.response.data.errors
                            ? ex.response.data.errors.qr
                                ? ex.response.data.errors.qr
                                : 'unknown'
                            : 'unknown';
                    this.error = err;
                    // drop the QR data so a reload does not read it again
                    this.navigateTo({ path: this.$route.path }, true);
                });
        },
        backgroundEventListImageUrl(name) {
            return CONFIG.IMAGE_BASE_URL + `/teamgeist/picture/event/${name}`;
        },
        onChangeOrder(optionKey) {
            let order;
            if (this.sortOption === optionKey) {
                //when sort option already selected, change sort order
                order = this.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                order = DEFAULT_SORT_ORDER[optionKey];
            }
            this.setSort(optionKey, order);
            if (optionKey === 'byDistance') {
                this.ensureDistanceSort();
            }
        },
        setSort(option, order) {
            this.$store.commit('events/SET_EVENT_SORT_OPTION', option);
            this.$store.commit('events/SET_EVENT_SORT_ORDER', order);
        },
        // distance sorting needs a position; without permission fall back to the checkin order like the native app
        ensureDistanceSort() {
            if (this.position) {
                return;
            }
            this.requestPosition().catch(() => {
                this.setSort('byLastUsed', DEFAULT_SORT_ORDER.byLastUsed);
                this.showGpsProblemDialog();
            });
        },
        onCodeReceived(qrData) {
            const errorKey = this.handleScannedQr(qrData);
            if (errorKey) {
                this.showSnackbarError(this.$t(errorKey));
            }
        },
        showSnackbarError(message) {
            this.snackbarText = message;
            this.snackbarColor = 'error';
            this.snackbarShow = true;
        },
        calculateRatio() {
            this.aspectRatioValue = this.ratio(screen.width, screen.height);
            document.documentElement.style.setProperty('--aspect-ratio', this.aspectRatioValue);
        },
        gcd(a, b) {
            if (b > a) {
                let temp = a;
                a = b;
                b = temp;
            }
            while (b != 0) {
                let m = a % b;
                a = b;
                b = m;
            }
            return a;
        },

        ratio(x, y) {
            let c = this.gcd(x, y);
            return '' + x / c + ':' + y / c;
        },
    },
};
</script>

<style lang="scss">
#event_list {
    .section-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: #ffffff;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    // stays visible at the bottom of the scrolling card content while the list scrolls
    .list-actions {
        position: sticky;
        bottom: 0;
        z-index: 2;
        background: #ffffff;
        align-items: center;
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.06);
    }

    .event {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        padding-top: 25px;

        .first-row {
            display: flex;
            width: 100%;

            @media screen and (max-width: 480px) {
                flex-direction: column;
            }

            .img-container {
                border-radius: 20px;

                @media screen and (max-width: 480px) {
                    width: 100%;
                }
                .image {
                    object-fit: cover;
                    width: 100px;
                    height: 100px;
                    border-radius: 20px;

                    @media screen and (max-width: 480px) {
                        // maybe use https://www.npmjs.com/package/v-aspect-ratio
                        width: 100%;
                        aspect-ratio: var(--aspect-ratio);
                        height: initial;
                        // object-fit: cover;
                    }
                }
            }

            .content {
                display: flex;
                margin-left: 40px;
                width: 100%;
                flex-direction: column;

                @media screen and (max-width: 480px) {
                    margin-left: 0px;
                }

                .title-container {
                    display: flex;
                    width: 100%;
                    justify-content: space-between;

                    @media screen and (max-width: 480px) {
                        margin-top: 50px;
                    }

                    .title {
                        font-weight: bold;
                    }

                    .countdown {
                        display: flex;
                    }
                }

                .usecase {
                    display: flex;
                    align-items: center;
                    margin-top: 4px;
                }
            }
        }

        .second-row {
            display: flex;
            width: 100%;

            .event-description {
                margin: 20px 15px 20px 15px;

                @media screen and (max-width: 480px) {
                    margin: 20px 5px 20px 0px;
                    width: 100%;
                }
            }
        }
    }
}
</style>
